import React, { useRef, useState } from "react";
import axios from "axios";
import StorefrontMarketCompare from "@/components/StorefrontMarketCompare";
import { ScanSearch, Upload, Loader2, CheckCircle2, XCircle, Clock, AlertTriangle, ShieldCheck, FileDown, Zap, MessageCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const LANGS = ["English", "Hindi", "Marathi", "Gujarati", "Tamil", "Telugu", "Kannada", "Bengali", "Punjabi", "Malayalam"];
const norm = (x) => (typeof x === "string" ? { title: x, simple: "" } : x || {});
const sColor = (s) => (s >= 75 ? "#059669" : s >= 50 ? "#f59e0b" : "#dc2626");

const Mini = ({ title, icon: Icon, items, tone }) => (
  items?.length > 0 && (
    <div className="bg-white rounded-xl border border-stone-200 p-4 text-left">
      <div className={`flex items-center gap-1.5 text-xs font-black uppercase mb-2 ${tone}`}><Icon className="w-3.5 h-3.5" /> {title}</div>
      {items.slice(0, 5).map((i, k) => {
        const it = norm(i);
        return <div key={k} className="mb-2"><div className="font-bold text-xs text-stone-900">{it.title}</div>{it.simple && <div className="text-[11px] text-stone-500">{it.simple}</div>}</div>;
      })}
    </div>
  )
);

export default function StorefrontPolicyCheck({ code, partnerName }) {
  const [f, setF] = useState({ name: "", phone: "", language: "English" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  const ref = useRef();

  const run = async () => {
    setErr("");
    if (f.name.trim().length < 2) return setErr("Please enter your name");
    if (f.phone.length !== 10) return setErr("Please enter a valid 10-digit mobile number");
    if (!file) return setErr("Attach your policy PDF");
    setBusy(true);
    try {
      const b64 = await new Promise((ok, no) => {
        const r = new FileReader(); r.onload = () => ok(r.result); r.onerror = no; r.readAsDataURL(file);
      });
      const { data: job } = await axios.post(`${API}/api/public/storefront/${code}/policy-lens`,
        { ...f, filename: file.name, data_base64: b64 }, { timeout: 60000 });
      // Poll until the background decode finishes (proxy-safe: no long-held request)
      const started = Date.now();
      let payload = null;
      while (Date.now() - started < 4 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data } = await axios.get(`${API}/api/public/policy-lens/${job.id}/status`);
        if (data.status === "failed") throw new Error(data.error || "Could not analyze — please try again");
        if (data.status === "done") { payload = data; break; }
      }
      if (!payload) throw new Error("Taking longer than usual — please try again in a minute");
      setRes(payload);
    } catch (e) { setErr(e.response?.data?.detail || e.message || "Could not analyze — please try again"); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    if (!res?.id) return;
    setPdfBusy(true);
    setErr("");
    try {
      const resp = await axios.get(`${API}/api/public/policy-lens/${res.id}/report.pdf`, { responseType: "blob", timeout: 90000 });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PolicyLens-${(res.result?.plan_name || "report").slice(0, 30).replace(/\s+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { setErr("Could not generate your PDF — please try again"); }
    finally { setPdfBusy(false); }
  };

  const r = res?.result;
  return (
    <section className="px-5 sm:px-8 py-12 bg-emerald-950 text-white" data-testid="storefront-policy-check">
      <div className="max-w-3xl mx-auto text-center">
        <span className="text-xs font-bold uppercase tracking-[0.25em] text-amber-400">Free · takes 1 minute</span>
        <h2 className="font-display text-3xl sm:text-4xl font-black mt-2 flex items-center justify-center gap-2">
          <ScanSearch className="w-8 h-8 text-amber-400" /> Free Policy Checkup
        </h2>
        <p className="text-sm text-emerald-200 mt-2">Do you actually know what your insurance covers? Upload it — get a plain-language score in your own language. No spam, ever.</p>

        {!r ? (
          <div className="mt-6 bg-white/10 backdrop-blur rounded-2xl p-5 space-y-3">
            <div className="grid sm:grid-cols-3 gap-2.5">
              <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your name"
                className="h-11 rounded-xl px-4 text-sm text-stone-900" data-testid="sf-lens-name" />
              <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="Mobile number"
                type="tel" inputMode="numeric" maxLength={10}
                className="h-11 rounded-xl px-4 text-sm text-stone-900" data-testid="sf-lens-phone" />
              <select value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })}
                className="h-11 rounded-xl px-3 text-sm text-stone-900 bg-white" data-testid="sf-lens-language">
                {LANGS.map((l) => <option key={l}>{l}</option>)}
              </select>
            </div>
            <input ref={ref} type="file" accept="application/pdf" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="sf-lens-file" />
            <div className="flex flex-wrap gap-2.5 justify-center">
              <button onClick={() => ref.current?.click()} className="h-11 rounded-full border-2 border-amber-400 text-amber-300 font-bold text-sm px-6" data-testid="sf-lens-pick">
                <Upload className="w-4 h-4 inline mr-1.5 -mt-0.5" /> {file ? file.name.slice(0, 26) : "Upload policy PDF"}
              </button>
              <button onClick={run} disabled={busy} className="h-11 rounded-full bg-amber-400 hover:bg-amber-300 text-emerald-950 font-black text-sm px-8 disabled:opacity-60" data-testid="sf-lens-run">
                {busy ? <><Loader2 className="w-4 h-4 inline animate-spin mr-1.5 -mt-0.5" /> Checking your policy…</> : "Get my free score"}
              </button>
            </div>
            {err && <div className="text-xs font-bold text-red-300" data-testid="sf-lens-error">{err}</div>}
            <div className="text-[10px] text-emerald-300">Your policy is analyzed securely and shared only with {partnerName}.</div>
          </div>
        ) : (
          <div className="mt-6 space-y-4" data-testid="sf-lens-result">
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 flex flex-wrap items-center gap-6 justify-center">
              <div className="relative w-32 h-32">
                <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                  <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,.15)" strokeWidth="11" />
                  <circle cx="60" cy="60" r="52" fill="none" strokeWidth="11" strokeLinecap="round" stroke={sColor(r.cps)} strokeDasharray={`${(r.cps / 100) * 326.7} 326.7`} />
                </svg>
                <div className="absolute inset-0 grid place-items-center"><div className="font-display text-4xl font-black">{r.cps}</div></div>
              </div>
              <div className="text-left max-w-md">
                <div className="font-display text-xl font-black leading-snug">"{r.verdict}"</div>
                <div className="text-xs text-emerald-200 mt-1.5">{r.plan_name} · {r.insurer}</div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {res.decode_seconds != null && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-black rounded-full px-2.5 py-1 bg-white/10 text-amber-300"><Zap className="w-3 h-3" /> Decoded in {res.decode_seconds}s</span>
                  )}
                  {res.grounded && <span className="inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2.5 py-1 bg-white/10 text-emerald-300"><ShieldCheck className="w-3 h-3" /> Verified against official policy wordings</span>}
                </div>
              </div>
            </div>
            {/* Policy summary strip */}
            <div className="bg-white rounded-2xl p-4 text-stone-900 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-2.5 text-left" data-testid="sf-lens-summary">
              {[["Policyholder", r.policy_holder], ["Insurer", r.insurer], ["Policy No.", r.policy_number],
                ["Type", r.policy_type], ["Sum Assured", r.sum_insured], ["Renews", r.renewal_date]]
                .filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="min-w-0">
                    <div className="text-[9px] font-bold uppercase tracking-wider text-stone-400">{k}</div>
                    <div className="text-xs font-bold truncate mt-0.5">{v}</div>
                  </div>
                ))}
            </div>
            <button onClick={downloadPdf} disabled={pdfBusy}
              className="h-11 rounded-full bg-amber-400 hover:bg-amber-300 text-emerald-950 font-black text-sm px-8 disabled:opacity-60"
              data-testid="sf-lens-download-pdf">
              {pdfBusy ? <><Loader2 className="w-4 h-4 inline animate-spin mr-1.5 -mt-0.5" /> Preparing your report…</>
                : <><FileDown className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Download my full report (PDF)</>}
            </button>
            <div className="text-[10px] text-emerald-300 -mt-2">Share the PDF on WhatsApp with family — it's in {f.language}.</div>
            {err && <div className="text-xs font-bold text-red-300" data-testid="sf-lens-pdf-error">{err}</div>}
            <div className="grid sm:grid-cols-2 gap-3">
              <Mini title="You're protected for" icon={CheckCircle2} items={r.covered} tone="text-emerald-700" />
              <Mini title="You're NOT protected for" icon={XCircle} items={r.not_covered} tone="text-red-600" />
              <Mini title="You'll have to wait for" icon={Clock} items={r.waiting} tone="text-amber-700" />
              <Mini title="Watch out for" icon={AlertTriangle} items={r.hidden_clauses} tone="text-orange-700" />
            </div>
            {(r.premium_breakdown?.annual_premium || r.premium_breakdown?.total || r.premium) && (
              <div className="bg-white rounded-xl border border-stone-200 p-4 text-left text-stone-900" data-testid="sf-lens-premium">
                <div className="text-xs font-black uppercase text-amber-700 mb-2">Premium breakdown</div>
                {[["Annual Premium", r.premium_breakdown?.annual_premium], ["GST", r.premium_breakdown?.gst],
                  ["Total Premium", r.premium_breakdown?.total || r.premium]].filter(([, v]) => v).map(([k, v], idx, arr) => (
                    <div key={k} className={`flex justify-between text-xs py-1 ${idx === arr.length - 1 ? "font-black border-t border-stone-100 pt-1.5" : "text-stone-500"}`}>
                      <span>{k}</span><span className="font-bold text-stone-900">{v}</span>
                    </div>
                  ))}
              </div>
            )}
            {r.gaps?.length > 0 && (
              <div className="bg-amber-400 text-emerald-950 rounded-2xl p-5 text-left" data-testid="sf-lens-gaps">
                <div className="font-black text-sm">⚠️ We found {r.gaps.length} gap{r.gaps.length > 1 ? "s" : ""} in your protection</div>
                <ul className="text-xs mt-2 space-y-1">{r.gaps.map((g, i) => <li key={i}>• {g.gap}</li>)}</ul>
                <div className="text-xs font-bold mt-3">{res.agent_name} will call you shortly to explain your report and how to fix these — free.</div>
              </div>
            )}
            <StorefrontMarketCompare analysisId={res.id} policyKind={res.policy_kind} agentName={res.agent_name} agentPhone={res.agent_phone} />
            {/* Talk to an expert */}
            <div className="bg-white/10 backdrop-blur rounded-2xl p-5 flex flex-wrap items-center gap-4 justify-between" data-testid="sf-lens-expert">
              <div className="text-left">
                <div className="font-display font-black text-base">Need help understanding something?</div>
                <div className="text-xs text-emerald-200">{res.agent_name} is your certified Crece consultant — just a message away.</div>
              </div>
              {res.agent_phone ? (
                <button
                  onClick={() => window.open(`https://wa.me/91${String(res.agent_phone).replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(`Hi ${res.agent_name}! I just checked my ${r.plan_name || "policy"} on your Crece page (scored ${r.cps}/100). Can you help me understand my report?`)}`, "_blank")}
                  className="h-10 rounded-full bg-white text-emerald-950 font-black text-sm px-6" data-testid="sf-lens-talk-expert">
                  <MessageCircle className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Talk to {res.agent_name?.split(" ")[0] || "an Expert"}
                </button>
              ) : (
                <span className="text-xs text-emerald-200">They&apos;ll call you shortly.</span>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
